<template>
  <div
    v-if="document"
    class="document-item d-flex align-items-center"
    :class="[
      {
        clickable: documentClickable,
      },
      displayOptions.size,
    ]"
    data-testid="document-item"
    @click="openDocument"
    @mouseenter="hovering = true"
    @mouseleave="hovering = ''"
  >
    <div
      class="document-thumbnail-wrapper d-flex flex-column align-items-center"
      :class="{
        'justify-content-center': !documentThumb,
        rejected:
          documentStatusEnabled &&
          [documentStatus.REJECTED].includes(document.review_status),
        accepted:
          documentStatusEnabled &&
          [documentStatus.ACCEPTED, documentStatus.AUTO_ACCEPTED].includes(
            document.review_status,
          ),
      }"
    >
      <div class="document-thumbnail">
        <image-with-loading
          :document="document"
          :loading="documentIsBeingUploaded"
          :img-src="documentThumb"
        ></image-with-loading>
      </div>

      <!--status icons-->
      <div class="document-status-icon">
        <i-icon
          v-if="
            documentStatusEnabled &&
            (document.review_status === documentStatus.AUTO_ACCEPTED ||
              document.review_status === documentStatus.ACCEPTED)
          "
          class="document-status-icon i-text-success-90"
          symbol="small-solid-check"
          :size="16"
        ></i-icon>
        <i-icon
          v-if="
            documentStatusEnabled &&
            document.review_status === documentStatus.REJECTED
          "
          class="document-status-icon i-text-error-90"
          symbol="small-solid-delete"
          :size="16"
        ></i-icon>

        <i-icon
          v-if="documentHasErrorStates"
          class="document-status-icon i-text-error-90"
          symbol="small-solid-alert-triangle"
          :size="16"
        ></i-icon>
      </div>
    </div>

    <div class="d-flex align-items-center w-100 document-detail-wrapper">
      <div class="d-flex flex-column flex-grow-1 overflow-hidden">
        <div
          class="title-container d-flex flex-row justify-content-start-center align-items-center"
        >
          <router-link
            v-if="documentHasContentToBeShown && displayOptions.clickable"
            data-testid="document-name"
            class="document-name mr-3 fs-exclude i-mb-1"
            :class="{
              disabled: documentIsBeingUploaded || documentHasErrorStates,
            }"
            :to="`/customer/${document.customer_id}/document/${
              document.id
            }/signals${
              displayOptions?.return_to
                ? `?return_to=${displayOptions.return_to}`
                : ``
            }`"
            @click.stop
          >
            <h6 class="title i-text-neutral-120 mb-0">
              {{ document.name }}
            </h6>
          </router-link>
          <h6
            v-if="!displayOptions.clickable"
            class="title i-text-neutral-120 mb-0"
          >
            {{ document.name }}
          </h6>
          <i-badge
            v-if="document.review_status === documentStatus.AUTO_ACCEPTED"
            :colour="iBadgeColours.SUCCESS"
            :outline="true"
            label="Auto-accepted"
            class="i-ml-4"
          ></i-badge>
        </div>
        <div class="d-flex align-items-center justify-content-between">
          <div
            v-if="document.state !== documentStates.PROCESSED"
            class="document-sub-status w-100 i-text-body-2"
          >
            <span v-if="document.state === documentStates.VIRUS_DETECTED">
              File not uploaded as a virus was found inside it.
            </span>
            <span v-if="document.state === documentStates.ERROR_PROCESSING">
              An error occurred while processing this document. Inscribe has
              been notified.
            </span>
            <span v-else-if="document.state === documentStates.IN_HUMAN_REVIEW">
              Queued for human review
            </span>
            <span
              v-else-if="document.state === documentStates.PASSWORD_PROTECTED"
            >
              This document is password protected and could not be processed
            </span>
            <span v-else-if="documentIsBeingUploaded"> Loading... </span>
            <span
              v-else-if="
                document.state === documentStates.EXCEEDS_MAX_PAGE_COUNT
              "
            >
              This document has more than 350 pages which is too long to
              process.
            </span>
            <p
              v-else-if="document.state === documentStates.ERROR_UPLOADING"
              class="my-2"
            >
              <span class="error mr-1">Error uploading.</span>
            </p>
          </div>
          <div
            v-else
            class="document-stats d-flex flex-wrap align-items-center"
          >
            <span v-if="hovering">View document</span>
            <span v-else-if="document.state === documentStates.PROCESSING"
              >View document</span
            >
            <div v-else>
              <span
                v-if="
                  document.document_type &&
                  document.document_type !== documentTypes.UNKNOWN
                "
                class="stats-icon"
              >
                {{ documentTypeText(document.document_type) }}
                <span v-if="document.file_type" class="mr-1">&bull;</span>
              </span>
              <span v-if="document.file_type">
                {{ documentFileTypeText(document.file_type) }}
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="displayOptions.currentDocLabel && isCurrentDoc"
          class="current-document-label i-mt-6"
        >
          <div class="i-pill i-text-subheading-3">
            <span class="neutral">Current Document</span>
          </div>
        </div>

        <!-- Fraud Signals & Tags -->
        <div
          v-if="
            !canviewDocumentReviewAgent &&
            suspiciousSignals.length > 0 &&
            !documentIsBeingUploaded
          "
          class="signals d-flex flex-row i-mt-6"
        >
          <signal-chip
            v-for="signal in visibleSignals"
            :key="signal.signalName"
            :signal="signal"
            class="default-signal"
          >
          </signal-chip>
          <div
            v-if="showHiddenSignalsMessage"
            class="floating-menu-container extra-signals-container layout-row align-items-center i-text-subheading-3 i-pl-4 i-pr-4 i-pt-2 i-pb-2"
            @mouseenter="moreSignalsHovering = true"
            @mouseleave="moreSignalsHovering = false"
            @click.stop="moreSignalsMenuVisible = !moreSignalsMenuVisible"
          >
            <div class="floating-menu-trigger signal">
              <span class="i-text-neutral-120">{{ hiddenSignalsMessage }}</span>
              <i-icon
                symbol="small-chevron-down"
                :size="16"
                :class="
                  moreSignalsHovering
                    ? `i-text-neutral-110`
                    : `i-text-neutral-90`
                "
              ></i-icon>
            </div>
            <div
              v-click-outside="hideMoreSignalsMenu"
              :class="{ visible: moreSignalsMenuVisible }"
              class="floating-menu flex-column"
            >
              <div
                class="signals hidden-signals vertical-list d-flex flex-column flex-grow-1"
              >
                <div class="title i-text-button-2 i-mb-4">Other Signals</div>
                <signal-chip
                  v-for="hiddenSignal in hiddenSignals"
                  :key="hiddenSignal.signalName"
                  :signal="hiddenSignal"
                  class="hidden-signal"
                >
                </signal-chip>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="
            displayOptions.tags &&
            document.state === documentStates.PROCESSED &&
            document.tags.length > 0
          "
          class="document-tags d-flex flex-row"
        >
          <tags :document="document" :show-remove-button="true" />
          <tags-selector
            :document="document"
            trigger-locator=".i-badge"
            :pre-selected-tags="document.tags"
          >
            <i-badge
              slot="trigger"
              colour="neutral"
              label="Manage Tags"
              :has-trailing-icon="true"
              icon="small-chevron-down"
              interactable
            />
          </tags-selector>
        </div>
      </div>
      <!-- Trust Score & Checks (& Document Risk Level (Agent) if enabled) -->
      <div
        v-if="documentHasContentToBeShown"
        class="d-flex align-items-center flex-shrink-0"
      >
        <span
          v-if="
            !canviewDocumentReviewAgent &&
            displayOptions.checks &&
            !checks.passed &&
            checks.total > 0 &&
            document.state === documentStates.PROCESSED
          "
        >
          <div class="document-checks d-flex align-items-center">
            <i-badge
              class="signal-chip"
              :colour="
                checks.passed ? iBadgeColours.SUCCESS : iBadgeColours.ERROR
              "
              :icon="
                checks.passed ? 'small-solid-check' : 'small-alert-triangle'
              "
              :has-leading-icon="true"
              :label="checksDescription.number + ' ' + checksDescription.text"
              :interactable="true"
            ></i-badge>
          </div>
        </span>

        <template v-if="!documentIsBeingUploaded && !documentHasErrorStates">
          <div
            v-if="!canviewDocumentReviewAgent"
            class="floating-menu-container trust-score-container"
          >
            <div class="floating-menu-trigger">
              <trust-score
                :score="document.trust_score"
                :size="displayOptions.trustScoreSize"
                :neutral="
                  [
                    documentStatus.ACCEPTED,
                    documentStatus.AUTO_ACCEPTED,
                    documentStatus.REJECTED,
                  ].includes(document.review_status)
                "
              />
            </div>
            <div
              v-if="displayOptions.trustScoreInfo"
              class="floating-menu trust-score flex-column"
            >
              <span class="header">Trust Score</span>
              <span class="content">
                A document’s Trust Score indicates how likely it is to be
                fraudulent. A higher score indicates the document is less likely
                to be fraudulent, while a lower score suggests it may be
                fraudulent.
              </span>
              <help-article-link
                help-article="5557654-trust-score"
                class="trust-score-help-link"
                :learn-more="true"
              />
            </div>
          </div>
          <div v-else class="floating-menu-container trust-score-container">
            <document-risk-level :document="document" />
          </div>
        </template>
      </div>
      <div v-if="documentIsBeingUploaded">
        <i-spinner size="xl" type="primary"></i-spinner>
      </div>
      <div v-else>
        <document-item-menu
          v-if="displayOptions.actionMenu"
          :doc="document"
          @document-deleted="onDocumentDeleted"
        ></document-item-menu>
      </div>
    </div>
  </div>
</template>
<script>
import * as documentStates from '@/constants/documentStates';
import * as documentStatus from '@/constants/documentStatus';
import * as documentTypes from '@/constants/documentTypes';
import { PREFERRED_SIGNAL_ORDER } from '@/constants/signalConfig';
import * as signalNames from '@/constants/signalNames';
import { calculateVerificationsAndChecks, getChecksText } from '@/utils/checks';
import Tags from '@/V2/documents/Tags';
import TagsSelector from '@/V2/components/TagsSelector.vue';
import TrustScoreDisplay from '@/V2/documents/TrustScoreDisplay';
import ImageWithLoading from '@/shared/components/ImageWithLoading';
import helpArticleLink from '@/V2/documents/HelpArticleLink';
import { mapGetters } from 'vuex';
import { iBadge, iBadgeColours, iIcon, iSpinner } from '@inscribe/signal';
import SignalChip from '@/V2/components/signals/SignalChip';
import DocumentItemMenu from '@/V2/documents/document-list/DocumentItemMenu';
import DocumentRiskLevel from '@/V2/documents/document-list/DocumentRiskLevel';
import { getDocumentTypeText } from '@/utils/documentType';
import { getFileTypeText } from '@/utils/fileType';

export default {
  components: {
    DocumentItemMenu,
    iBadge,
    iIcon,
    iSpinner,
    ImageWithLoading,
    'help-article-link': helpArticleLink,
    SignalChip,
    TagsSelector,
    Tags,
    'trust-score': TrustScoreDisplay,
    DocumentRiskLevel,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      documentStates,
      documentStatus,
      documentTypes,
      iBadgeColours,
      isDownloading: false,
      isDropdownOpen: false,
      showTrustMenu: false,
      signalNames,
      hovering: false,
      moreSignalsHovering: false,
      moreSignalsMenuVisible: false,
      displayOptions: Object.assign(
        {
          currentDocLabel: false,
          size: 'default',
          actionMenu: true,
          checks: true,
          tags: true,
          trustScoreSize: 'default',
          trustScoreInfo: true,
          defaultNumSignalsVisible: 3,
          clickable: true,
        },
        this.options,
      ),
    };
  },
  computed: {
    ...mapGetters({
      documentStatusEnabled: 'documentStatusEnabled',
      canviewDocumentReviewAgent: 'canViewDocumentReviewAgent',
    }),
    documentClickable() {
      return (
        this.document.state === documentStates.PROCESSED &&
        this.displayOptions.clickable
      );
    },
    isCurrentDoc() {
      return this.document.id === this.$route.params.document_id;
    },
    visibleSignals() {
      return this.suspiciousSignals.slice(
        0,
        !this.showHiddenSignalsMessage
          ? this.suspiciousSignals.length
          : this.displayOptions.defaultNumSignalsVisible,
      );
    },
    showHiddenSignalsMessage() {
      return (
        this.suspiciousSignals.length >
        this.displayOptions.defaultNumSignalsVisible + 1
      );
    },
    numberOfHiddenSignals() {
      return this.suspiciousSignals.length - this.visibleSignals.length;
    },
    hiddenSignalsMessage() {
      return `${this.numberOfHiddenSignals} more`;
    },
    hiddenSignals() {
      return this.suspiciousSignals.slice(-this.numberOfHiddenSignals);
    },

    documentThumb() {
      return this.document?.urls?.thumbnail_small || '';
    },

    checks() {
      return calculateVerificationsAndChecks(this.document, true);
    },
    verificationSignals() {
      return [
        signalNames.NAME,
        signalNames.ADDRESS,
        signalNames.COMPANY,
        signalNames.ID_NUMBER,
        signalNames.STRINGS,
      ].filter((key) => Object.keys(this.document.verification).includes(key));
    },

    suspiciousSignals() {
      return Object.entries(this.document.fraud_results || [])
        .reduce((signals, [signalName, signal]) => {
          if (signal.failed) {
            return [
              ...signals,
              {
                signalName,
                failed: true,
                type: signal.type,
                ...signal.details,
              },
            ];
          }

          return signals;
        }, [])
        .sort(
          (signalA, signalB) =>
            PREFERRED_SIGNAL_ORDER.indexOf(signalA.signalName) -
            PREFERRED_SIGNAL_ORDER.indexOf(signalB.signalName),
        );
    },

    fileTypeIcon() {
      if (this.document.file_type === 'TRUE_PDF') return 'file-pdf';
      else if (this.document.file_type === 'IMAGE') return 'image';
      else return 'file';
    },

    checksDescription() {
      return getChecksText(
        this.checks?.passed,
        this.checks?.passedCount,
        this.checks?.total,
      );
    },

    documentHasErrorStates() {
      return [
        documentStates.ERROR_PROCESSING,
        documentStates.ERROR_UPLOADING,
      ].includes(this.document.state);
    },

    documentIsBeingUploaded() {
      return [documentStates.UPLOADING, documentStates.PROCESSING].includes(
        this.document.state,
      );
    },

    documentHasContentToBeShown() {
      return [
        documentStates.UPLOADING,
        documentStates.PROCESSED,
        documentStates.PROCESSING,
        documentStates.ERROR_PROCESSING,
        documentStates.ERROR_UPLOADING,
      ].includes(this.document.state);
    },
  },
  methods: {
    documentTypeText: getDocumentTypeText,
    documentFileTypeText: getFileTypeText,
    hideMoreSignalsMenu() {
      this.moreSignalsMenuVisible = false;
    },
    onDocumentDeleted(docData) {
      this.$emit('document-deleted', docData);
    },
    openDocument(evt) {
      if (this.documentClickable && !this.isNewTabKey(evt)) {
        const query = {};
        if (this.displayOptions?.return_to) {
          query.return_to = this.displayOptions.return_to;
        }
        this.$router
          .push({
            name: 'document-signals',
            params: {
              customer_id: this.document.customer_id,
              document_id: this.document.id,
            },
            query,
          })
          .catch(() => {});
        this.$emit('open-document');
      }
    },
    isNewTabKey(evt) {
      return evt.metaKey || evt.ctrlKey || evt.shiftKey;
    },
  },
};
</script>

<style lang="scss" scoped>
$item-height: 150px;
$borderRadiusSize: 16px;

.submenu-style {
  background: $color-white;
  border: 1px solid $light-neutral-60;
  border-radius: 16px;
  position: absolute;
  display: flex;
  z-index: 1020;
}

.document-item {
  border-bottom: 1px solid $light-neutral-60;
  background-color: $color-white;
  min-height: $item-height;
  position: relative;
  transition: background-color 150ms ease-in-out;

  &:first-of-type {
    border-top-left-radius: $borderRadiusSize;
    border-top-right-radius: $borderRadiusSize;
  }

  &:last-of-type {
    border-bottom-left-radius: $borderRadiusSize;
    border-bottom-right-radius: $borderRadiusSize;
    border-bottom: 0;
  }

  &.clickable {
    cursor: pointer;
    &:hover {
      background: $light-neutral-10;
    }
  }

  &.small {
    .document-thumbnail-wrapper {
      width: 88px;
      .document-thumbnail {
        max-width: 45px;
        :deep(img) {
          max-height: 65px;
        }
      }
    }

    .document-stats {
      font-size: 12px;
      line-height: 16px;
    }
    .document-name span {
      font-size: 14px;
      line-height: 20px;
      max-width: 275px;
    }

    .document-detail-wrapper {
      padding: 20px;
    }

    .trust-score-container {
      margin-right: 0;
    }
  }

  .document-thumbnail-wrapper {
    position: relative;
    width: 140px;
    margin: 12px;
    border-radius: 8px;
    flex-shrink: 0;
    background-color: $light-neutral-30;
    align-self: stretch;
    overflow: hidden;
    justify-content: center;

    &.rejected {
      background: $light-error-30;
    }

    &.accepted {
      background: $light-success-30;
    }

    @include respond-to('medium') {
      display: none !important;
    }

    .document-thumbnail {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      height: 140px;
      max-width: 80px;

      :deep(img) {
        border: 1px solid $light-neutral-60;
        max-height: 104px;
        width: 100%;
        object-fit: cover;
        object-position: top;
      }
    }

    .document-status-icon {
      position: absolute;
      bottom: 4px;
      right: 4px;
    }
  }

  .document-sub-status {
    color: $light-neutral-110;
    font-size: $font-size-default;
  }

  .document-detail-wrapper {
    min-width: 0;
    padding: 24px;
  }
  .document-name {
    display: -ms-grid;
    display: grid;
    font-weight: bold;

    &.disabled {
      pointer-events: none;
      text-decoration: none;
    }

    .title {
      font-weight: 700;
    }
  }

  .document-checks {
    font-size: $font-size-secondary;
    font-weight: 500;
    letter-spacing: -0.01rem;
    padding: 3px 10px 3px 10px;
    border-radius: 24px;
    border-radius: 100px;
    gap: 6px;

    .check-text {
      color: $light-error-120;

      @include respond-to('medium') {
        display: none !important;
      }
    }
  }
  .document-date {
    color: $color-navy-blue-40;
    white-space: nowrap;
  }

  .document-stats {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $light-neutral-110;
    text-transform: capitalize;

    :deep(.icon-stat) {
      svg {
        &.icon-stat-icon {
          margin-right: 8px !important;
        }
      }
    }
  }

  .floating-menu {
    box-shadow: 0px 16px 32px 4px rgba(0, 0, 0, 0.08);
    margin-top: 4px;
  }

  .signals {
    flex-wrap: wrap;
    column-gap: 16px;
    gap: 6px;
    margin-top: 12px;

    a:hover {
      text-decoration: none;
    }

    &.vertical-list {
      gap: 8px;
      margin-top: 0;
    }

    .floating-menu-container .floating-menu {
      margin-top: 8px;
      padding: 16px;

      a {
        display: flex;
      }
    }
  }

  .trust-score-container {
    position: relative;
    margin: 0 16px;

    .floating-menu {
      width: 330px;
      right: 0;

      .header {
        font-style: normal;
        font-weight: 600;
        font-size: 12px;
        line-height: 18px;
        color: $light-neutral-120;
      }

      .content {
        margin-top: 4px;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        color: $light-neutral-110;
      }
    }
  }

  .floating-menu-container {
    &.extra-signals-container {
      height: 22px;
      background: $light-neutral-30;
      border-radius: 100px;

      .floating-menu {
        width: 230px;
      }

      &:hover {
        background: $light-neutral-40;
      }
    }

    .floating-menu-trigger {
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 100%;

      &.signal {
        font-weight: 500;
        color: $light-neutral-110;
        font-size: $font-size-secondary;
        line-height: initial;
      }
    }

    &:active,
    &:hover,
    &:focus {
      .floating-menu.trust-score {
        visibility: visible;
      }
    }

    .floating-menu {
      padding: 20px;
      visibility: hidden;
      @extend .submenu-style;

      &.visible {
        visibility: visible;
      }
    }
  }
  .document-tags {
    margin-top: 8px;
  }
}
</style>
